/**
 * 부팅이 문서(document·window)에 닿는 네 자리.
 *
 * **조립은 `main.ts`가 한다.** 여기 모은 것은 조립 전후에 브라우저가 준 것을 만지는 잡일뿐이다 -
 * 캔버스를 찾고, 탭 제목과 문서 언어를 맞추고, 배경음의 첫 제스처를 걸고, 실패하면 배너를 띄운다.
 *
 * 넷 다 **앱의 상태를 읽지 않는다.** 어느 화면인지·어느 무대인지 모르는 채로 돌 수 있어야
 * 부팅이 실패한 뒤에도(무대가 없는 시점) 배너를 띄울 수 있다.
 */

import { bgm } from '../audio/bgm'
import { getLocale, t } from '../i18n'

/** index.html이 미리 놓아 둔 캔버스의 id. 렌더 레이어 계약이다. */
const CANVAS_ELEMENT_ID = 'app-canvas'

export function resolveCanvas(): HTMLCanvasElement {
  const element = document.getElementById(CANVAS_ELEMENT_ID)
  if (element instanceof HTMLCanvasElement) return element

  throw new Error(
    `#${CANVAS_ELEMENT_ID} 캔버스를 찾지 못했다. index.html의 렌더 레이어 계약이 깨졌다.`,
  )
}

/**
 * 문서 바깥쪽(탭 제목 · 문서 언어)을 지금 언어에 맞춘다.
 *
 * `lang`은 화면 낭독기가 발음 규칙을 고르는 근거다. `lang="ko"`인 문서에서 영어 문장을
 * 읽으면 통째로 알아들을 수 없는 소리가 난다. 제목은 탭 목록과 공유 링크에 그대로 나가는
 * 값이라, 화면은 영어인데 탭만 한국어인 상태를 남기지 않는다.
 * index.html이 적어 둔 값은 스크립트가 돌기 전의 기본값이므로 부팅에서 한 번 덮고,
 * 이후에는 언어가 바뀔 때마다 맞춘다(`main.ts`의 `onLocaleChange`).
 */
export function syncDocumentMeta(): void {
  document.documentElement.lang = getLocale()
  document.title = t('common.appName')
}

/** 첫 제스처로 인정하는 이벤트. 폰(터치)·PC(클릭)·키보드를 모두 덮는다(`haptics.ts`와 같은 목록). */
const UNLOCK_EVENTS: readonly string[] = ['pointerdown', 'touchend', 'keydown']

/**
 * 배경음의 첫 제스처.
 *
 * 효과음 엔진은 자기 것을 스스로 건다(`audio.installUnlockGesture`). 배경음은 **별도의
 * 재생기**라 자기 해제가 따로 필요하고, 그걸 부트스트랩이 쥐는 이유는 `?level=7` 직행 때문이다 -
 * 그 경로에는 제스처가 하나도 없이 판이 시작되므로, 여기서 걸어 두지 않으면 첫 탭이 올 때까지
 * AudioContext가 정지 상태로 남아 배경음이 통째로 침묵한다(단계 시작 버튼을 거친 경우에는
 * `startLevel`이 이미 그 클릭 안에서 열어 둔다).
 */
export function installBgmUnlockGesture(): () => void {
  const handler = (): void => {
    bgm.unlock()
    remove()
  }
  const remove = (): void => {
    for (const name of UNLOCK_EVENTS) window.removeEventListener(name, handler)
  }

  for (const name of UNLOCK_EVENTS) window.addEventListener(name, handler)
  return remove
}

/**
 * 부트스트랩 실패를 화면에 띄운다.
 *
 * WebGL 차단·하드웨어 가속 꺼짐은 흔한 실패이고(scene.ts), 그때 화면은 그냥 검게 남는다.
 * 콘솔만 보고 원인을 아는 사람은 개발자뿐인데, 4주차 플레이테스트에는 지인 6명이 들어온다 -
 * "안 켜져요"라는 보고만 받으면 원격으로 아무것도 할 수 없다.
 */
export function reportFatalError(error: unknown): void {
  const banner = document.createElement('div')
  banner.setAttribute('role', 'alert')
  banner.style.cssText = [
    'position:fixed',
    'inset:0',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    /*
     * 색은 **토큰에서 받는다**(`ui/gameHud.css`의 `:root`). 손으로 적어 둔 `#1a1a1a`·`#f4d7dc`는
     * 어느 화면과도 같지 않은 세 번째·네 번째 색이었고, 그래서 이 배너만 다른 앱처럼 보였다.
     * 그 스타일시트는 정적 import라(`main.ts` → `app/stage.ts` → `session.ts` → `gameHud.ts`)
     * 이 함수가 도는 시점에는 언제나 도착해 있다 - 부트스트랩이 실패해도 모듈 평가는 이미 끝난 뒤다.
     */
    'background:var(--gh-ink)',
    'color:var(--gh-fg)',
    'font:14px/1.6 system-ui, sans-serif',
    'text-align:center',
    'white-space:pre-wrap',
    'z-index:100',
  ].join(';')
  /*
   * 문구는 사전에서 온다. 예외 메시지 자체는 계약 위반의 이름이라 번역 대상이 아니고
   * (`#app-canvas를 찾지 못했다` 같은 것), 그대로 두어야 제보를 받았을 때 검색이 된다.
   * 그래서 사람에게 하는 말 두 줄과 기계가 남긴 한 줄을 눈에 보이게 갈라 둔다.
   */
  const detail = error instanceof Error ? error.message : String(error)
  banner.textContent = `${t('error.bootFailed')}\n${t('error.bootDetail')}\n\n${detail}`

  document.body.append(banner)
}
